import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useLocation, useParams, useSearchParams } from "react-router-dom";
import { createPortal } from "react-dom";
import axios from "axios";
import "@/App.css";
import { Package, ArrowLeft, ArrowClockwise, WarningCircle, CaretUp, CaretDown, MagnifyingGlass, DownloadSimple, Shield, MapPin, Printer } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Toaster, toast } from "@/components/ui/sonner";
import { NavTabs } from "@/components/NavTabs";
import { SapConnectionStatus } from "@/components/SapConnectionStatus";
import { ErpConnectionStatus } from "@/components/ErpConnectionStatus";
import { useAuth } from "@/contexts/AuthContext";
import { RequestPrintSlip } from "@/components/RequestPrintSlip";
import { StoreReturnsPanel } from "@/components/StoreReturnsPanel";
import { ReturnDetailDialog } from "@/components/ReturnToStoreTab";
import { ReturnPrintSlip } from "@/components/ReturnPrintSlip";
import { AdminAdjustmentPanel } from "@/components/AdminAdjustmentPanel";
import { formatUnit } from "../lib/formatUnit";
import { RoundedUpFlag } from "../components/RoundedUpFlag";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SITE_FILTER_KEY = "storeApprovalSelectedSite";

// 3 decimals: a store request now carries SAP's own BOM figure (0.512 of a
// pallet), and truncating it to 0.51 hides exactly what the user asked to see.
const formatQty = (v) => (v == null ? "\u2014" : Number(v).toLocaleString("en-IN", { maximumFractionDigits: 3 }));

// SAP's own BOM data returns "MASS" as a dimension/QuantityTypeCode label,
// not a real unit - matches the same display-only fix on Production
// Confirmation/Create Order screens. Internal values (unit_code sent back
// to the API, CSV export raw data) are untouched.

// Aug 2026, user's explicit ask: raw SAP logistics area IDs like
// "P1/P1-RM" are meaningless to a store person - show "P1 - Raw Material
// (RM)" everywhere one of these IDs is displayed (Issued From column,
// the fixed movement notice, Movement History table). Display-only -
// the underlying warehouse_id values sent to the API are untouched.
const WAREHOUSE_TYPE_LABELS = { RM: "Raw Material (RM)", QC: "Quality Hold (QC)", SFG: "Semi-Finished Goods (SFG)", FG: "Finished Goods (FG)" };
// Full-ID overrides for warehouses that don't fit the generic
// "{site}-{TYPE}" pattern the splitter below assumes - keep in sync with
// the backend's _SITE_RM_WAREHOUSE_OVERRIDE (store_approval_service.py).
const WAREHOUSE_ID_LABELS = { "P3/P3-Z1-01-A": "P3 - Raw Material Zone 1-01-A (RM)" };
const humanizeWarehouseId = (id) => {
  if (!id) return id;
  if (WAREHOUSE_ID_LABELS[id]) return WAREHOUSE_ID_LABELS[id];
  const raw = id.includes("/") ? id.split("/").pop() : id; // "P1/P1-RM" -> "P1-RM"
  const dashIdx = raw.lastIndexOf("-");
  if (dashIdx === -1) return raw;
  const site = raw.slice(0, dashIdx);
  const suffix = raw.slice(dashIdx + 1);
  return `${site} - ${WAREHOUSE_TYPE_LABELS[suffix] || suffix}`;
};
// Aug 2026, user's explicit ask: site P3 has no standard "-RM" warehouse
// in SAP at all - its raw material stock lives entirely in this one
// zone/bin warehouse instead (confirmed live against SAP). Mirrors the
// backend's _SITE_RM_WAREHOUSE_OVERRIDE (store_approval_service.py) -
// keep both in sync if this ever changes.
const SITE_RM_WAREHOUSE_OVERRIDE = { P3: "P3-Z1-01-A" };
const rmWarehouseIdForSite = (siteId) => `${siteId}/${SITE_RM_WAREHOUSE_OVERRIDE[siteId] || `${siteId}-RM`}`;

// Aging (Aug 2026, user's explicit ask): "time since requested" text +
// severity tier, reused for the Pending Queue's live badge and the
// Movement History report's "Age at Issue" column/CSV export.
const ageParts = (fromIso, toIso) => {
  if (!fromIso) return null;
  const ms = (toIso ? new Date(toIso) : new Date()) - new Date(fromIso);
  if (Number.isNaN(ms) || ms < 0) return null;
  const mins = Math.floor(ms / 60000);
  const hours = Math.floor(mins / 60);
  const days = Math.floor(hours / 24);
  const label = days > 0 ? `${days}d ${hours % 24}h` : hours > 0 ? `${hours}h ${mins % 60}m` : `${mins}m`;
  const tier = days > 0 ? "overdue" : hours >= 4 ? "warn" : "fresh";
  return { label, tier, hours: ms / 3600000 };
};

const AGE_TIER_CLASS = { fresh: "text-[#027A48]", warn: "text-[#B54708] font-bold", overdue: "text-[#B42318] font-bold" };

// Aug 2026, user's ask: is this component actually a raw material, or a
// manufactured (semi-finished) part just being stocked/issued through the
// RM warehouse? Backend derives this from the SAP BOM cache (see
// store_approval_service.refresh_component_locations) - true/false/null
// (never checked yet in SAP). Display-only for now (user's explicit
// choice: "add a badge for now, let us check reliability" before any
// hiding logic).
const MaterialTypeBadge = ({ isManufactured, testId }) => {
  if (isManufactured == null) return null;
  return isManufactured ? (
    <Badge className="ml-1.5 bg-[#EFF8FF] text-[#175CD3] border-[#B2DDFF] border text-[10px] font-bold align-middle" data-testid={testId}>Manufactured</Badge>
  ) : (
    <Badge className="ml-1.5 bg-[#FFFAEB] text-[#B54708] border-[#FEDF89] border text-[10px] font-bold align-middle" data-testid={testId}>Bought-Out (RM)</Badge>
  );
};

const AgeBadge = ({ fromIso, toIso, testId }) => {
  const age = ageParts(fromIso, toIso);
  if (!age) return <span className="text-[#98A2B3]">\u2014</span>;
  return <span className={AGE_TIER_CLASS[age.tier]} data-testid={testId}>{age.label}{!toIso ? " ago" : ""}</span>;
};

const toCsv = (rows, columns) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const header = columns.map((c) => esc(c.label)).join(",");
  const body = rows.map((r) => columns.map((c) => esc(c.get(r))).join(",")).join("\n");
  return `${header}\n${body}`;
};

const downloadCsv = (filename, csv) => {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

// Aug 2026, matches production_confirmation_service.is_usable_stock_status()
// on the backend - SAP's stock_status can carry a real "Inspection"
// (Quality Inspection hold)/"Blocked" value that physically sits in a
// warehouse but is NOT usable for production. User's explicit ask after
// testing: show this directly on THIS screen (not just discover it after
// a failed/rejected SAP movement).
const RESTRICTED_STOCK_STATUSES = new Set(["inspection", "quality inspection", "blocked", "restricted-use", "restricted", "in transit"]);
const isRestrictedStatus = (status) => RESTRICTED_STOCK_STATUSES.has((status || "").trim().toLowerCase());
// Aug 2026 bug fix: a real SAP row can report a totally normal
// stock_status ("Not Assigned") while ALSO being flagged Restricted Use
// (SAP's own "Restr." checkbox on Stock Overview, CRESTRICTED_IND on the
// backend) - a SEPARATE field from stock_status, not another status
// value. Missed entirely until caught against a real 1kg restricted lot
// at site P2 that showed as plain usable stock on this screen.
const isLocationRestricted = (loc) => isRestrictedStatus(loc?.stock_status) || !!loc?.restricted;
// Aug 2026: only RM + QC (Quality Hold) warehouse rows ever reach this
// screen now (see store_approval_service.refresh_component_locations) -
// a QC-warehouse row reports stock_status "Not Assigned" in SAP's own
// feed (NOT "Inspection"), so it would otherwise slip through the check
// above as if it were normal usable stock. Being in the QC warehouse AT
// ALL means it's on hold, regardless of what stock_status says.
const isQcWarehouse = (warehouseId) => (warehouseId || "").endsWith("-QC");

const LocationBreakdown = ({ locations, unit, sourceWarehouseId }) => {
  if (!locations || locations.length === 0) {
    return <span className="text-[#98A2B3]">no stock at this site</span>;
  }
  const rmHasUsableStock = locations.some((loc) => loc.warehouse_id === sourceWarehouseId && !isLocationRestricted(loc));
  const rmHasOnlyRestrictedStock = !rmHasUsableStock && locations.some((loc) => loc.warehouse_id === sourceWarehouseId && isLocationRestricted(loc));
  return (
    <div className="space-y-0.5">
      {locations.map((loc, i) => {
        // Requests created before the warehouse/stock_status fields were
        // added froze the OLD {site, qty} shape into their doc forever -
        // fall back to the (unscoped) site code instead of a confusing
        // "Unknown Warehouse" for those legacy, already-open requests.
        const label = loc.warehouse || (loc.site ? loc.site.split("-").pop() : "Unknown Warehouse");
        const restricted = isLocationRestricted(loc) || isQcWarehouse(loc.warehouse_id);
        return (
          <div key={i} data-testid={restricted ? "location-restricted-row" : undefined}>
            <span className={restricted ? "text-[#B54708] font-bold" : "text-[#667085]"}>
              {label}{loc.stock_status ? ` (${loc.stock_status})` : ""}{restricted && !isRestrictedStatus(loc.stock_status) && !isQcWarehouse(loc.warehouse_id) ? " (Restricted Use)" : ""}{restricted ? " \u26A0 On Hold - not usable" : ""}:
            </span> {formatQty(loc.qty)} {formatUnit(unit)}
          </div>
        );
      })}
      {rmHasOnlyRestrictedStock && (
        <div className="text-[#B42318] font-bold" data-testid="rm-restricted-warning">
          {"\u26A0 RM stock is only on Quality Hold - not usable, will be treated as no stock to issue from."}
        </div>
      )}
    </div>
  );
};

const STATUS_BADGE = {
  pending: { label: "Awaiting Store", tone: "bg-[#FFFAEB] text-[#B54708] border-[#FEDF89]" },
  issuing: { label: "Issuing Stock...", tone: "bg-[#EFF8FF] text-[#0E7C86] border-[#B2DDFF]" },
  awaiting_receipt: { label: "Awaiting Requester Receipt", tone: "bg-[#FEF6EE] text-[#B93815] border-[#F9DBAF]" },
  partial_pending_planner: { label: "Awaiting Requester", tone: "bg-[#EFF8FF] text-[#175CD3] border-[#B2DDFF]" },
  resolved_balance_pending: { label: "Balance Pending", tone: "bg-[#FEF6EE] text-[#B93815] border-[#F9DBAF]" },
  resolved: { label: "Resolved", tone: "bg-[#ECFDF3] text-[#027A48] border-[#ABEFC6]" },
  cancelled: { label: "Cancelled", tone: "bg-[#FEF3F2] text-[#B42318] border-[#FECDCA]" },
};

// Free-text search across every field that matters, not just Material -
// user's explicit ask ("search by any field").
const matchesSearch = (r, term) => {
  if (!term) return true;
  const haystack = [
    r._id, r.issue_id, r.material_id, r.site_id, r.requester, r.production_proposal_id, r.status,
    r.store_actor, r.planner_actor,
    ...(r.components || []).flatMap((c) => [c.product_id, c.description]),
  ].filter(Boolean).join(" ").toLowerCase();
  return haystack.includes(term.toLowerCase());
};

// Sep 10 2026: same free-text-search idea as matchesSearch above, but
// for the Journal's resolved/rejected Return rows (different field
// shape - items[] instead of components[], no material_id).
const returnMatchesSearch = (r, term) => {
  if (!term) return true;
  const haystack = [
    r._id, r.original_request_id, r.site_id, r.requester, r.status,
    ...(r.items || []).flatMap((it) => [it.product_id, it.description]),
  ].filter(Boolean).join(" ").toLowerCase();
  return haystack.includes(term.toLowerCase());
};

const SortableHeader = ({ label, field, sortField, sortDir, onSort }) => (
  <th
    className="bg-[#EAECF0] border border-[#D0D5DD] p-1.5 text-left text-xs font-bold text-[#344054] font-heading uppercase tracking-wide cursor-pointer select-none"
    onClick={() => onSort(field)}
    data-testid={`store-journal-sort-${field}`}
  >
    <span className="inline-flex items-center gap-1">
      {label}
      {sortField === field && (sortDir === "asc" ? <CaretUp size={11} weight="bold" /> : <CaretDown size={11} weight="bold" />)}
    </span>
  </th>
);

const StoreTabStrip = ({ viewMode, navigate }) => (
  <div className="flex gap-1 bg-white border border-[#D0D5DD] rounded-sm p-1 flex-wrap">
    <button type="button" onClick={() => navigate("/storeapproval")} className={`px-3 py-1.5 text-xs font-bold rounded-sm ${viewMode === "queue" ? "bg-[#0E7C86] text-white" : "text-[#344054]"}`} data-testid="store-view-mode-queue">
      Pending Store Request
    </button>
    <button type="button" onClick={() => navigate("/storeapproval/returns")} className={`px-3 py-1.5 text-xs font-bold rounded-sm ${viewMode === "returns" ? "bg-[#0E7C86] text-white" : "text-[#344054]"}`} data-testid="store-view-mode-returns">
      Pending Store Return
    </button>
    <button type="button" onClick={() => navigate("/storeapproval/journal")} className={`px-3 py-1.5 text-xs font-bold rounded-sm ${viewMode === "journal" ? "bg-[#0E7C86] text-white" : "text-[#344054]"}`} data-testid="store-view-mode-journal">
      Journal (All Requests)
    </button>
    <button type="button" onClick={() => navigate("/storeapproval/balance")} className={`px-3 py-1.5 text-xs font-bold rounded-sm ${viewMode === "balance" ? "bg-[#0E7C86] text-white" : "text-[#344054]"}`} data-testid="store-view-mode-balance">
      Balance Pending
    </button>
    <button type="button" onClick={() => navigate("/storeapproval/movements")} className={`px-3 py-1.5 text-xs font-bold rounded-sm ${viewMode === "movements" ? "bg-[#0E7C86] text-white" : "text-[#344054]"}`} data-testid="store-view-mode-movements">
      Movement History
    </button>
  </div>
);

export default function StoreApprovalPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { requestId } = useParams();
  const [searchParams] = useSearchParams();
  // Aug 2026, user's explicit ask: Store Approval now requires Entra ID
  // login (was previously anonymous with a manual "Your Name" box) - the
  // acting store person's name comes straight from their signed-in
  // session instead.
  const { user } = useAuth();
  const storeActorName = user?.name || user?.email || "";
  // Aug 2026, user's ask: "always load what they select once" - the
  // chosen site persists across visits instead of resetting to "All
  // Sites" every time.
  const [siteFilter, setSiteFilter] = useState(() => localStorage.getItem(SITE_FILTER_KEY) || "all");
  // Aug 2026, user's ask: real sub-routes instead of hidden component
  // state, so a specific tab or a request's detail is shareable/
  // bookmarkable and survives browser back/forward + a page refresh.
  // viewMode is the underlying LIST tab (queue/journal/balance/movements)
  // - kept even while a request's detail is open (carried via the
  // ?from= query param on /storeapproval/request/:id) so "Back to queue"
  // returns to the right tab and the background 8s poll further below
  // keeps that tab's data fresh the whole time the detail view is open.
  const viewMode = requestId
    ? (searchParams.get("from") || "queue")
    : location.pathname.endsWith("/journal") ? "journal"
    : location.pathname.endsWith("/balance") ? "balance"
    : location.pathname.endsWith("/movements") ? "movements"
    : location.pathname.endsWith("/returns") ? "returns"
    : "queue";
  const listPath = (mode) => (mode === "queue" ? "/storeapproval" : `/storeapproval/${mode}`);
  const [requests, setRequests] = useState([]);
  const [journalRequests, setJournalRequests] = useState([]);
  const [balanceRequests, setBalanceRequests] = useState([]);
  const [returnJournal, setReturnJournal] = useState([]);
  // Jun 2026 bug fix: Movement History rows now come from the backend
  // (/store-requests/movement-history), the same collections the Movement
  // Audit Trail reads - see loadMovementHistory below.
  const [movementHistory, setMovementHistory] = useState([]);
  const [movementsLoading, setMovementsLoading] = useState(true);
  const [movementsError, setMovementsError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [issuedQty, setIssuedQty] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitElapsed, setSubmitElapsed] = useState(0);
  const [issueProgress, setIssueProgress] = useState(null);
  const [refreshingStock, setRefreshingStock] = useState(false);
  const [refreshElapsed, setRefreshElapsed] = useState(0);
  const [refreshStatus, setRefreshStatus] = useState(null);
  const [showResumeOption, setShowResumeOption] = useState(false);
  const [resuming, setResuming] = useState(false);
  const [resultMessage, setResultMessage] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortField, setSortField] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  // Sep 10 2026, user's explicit ask: a Resolved/Rejected Return to
  // Store should surface here on "Journal (All Requests)" as a View-only
  // row, not linger on the "Pending Store Return" tab (that tab now only
  // ever shows actionable pending/under_verification returns).
  const [viewingReturn, setViewingReturn] = useState(null);
  const [printReturnTarget, setPrintReturnTarget] = useState(null);
  // NPD requests only (Oct 2026): which godown each line is issued FROM.
  // Every other request is fixed to the site's RM warehouse by rule.
  const [sourceWarehouse, setSourceWarehouse] = useState({});

  useEffect(() => localStorage.setItem(SITE_FILTER_KEY, siteFilter), [siteFilter]);

  useEffect(() => {
    if (!printReturnTarget) return;
    const t = setTimeout(() => window.print(), 50);
    return () => clearTimeout(t);
  }, [printReturnTarget]);
  useEffect(() => {
    const clear = () => setPrintReturnTarget(null);
    window.addEventListener("afterprint", clear);
    return () => window.removeEventListener("afterprint", clear);
  }, []);

  // Ticks while a Goods Movement POST is in flight (backend now retries up
  // to 3x with a 5s backoff on transient SAP errors, so this single
  // request can occasionally take 30-70s - user's explicit ask was to show
  // a progress bar + message so it never looks frozen the way "Failed -
  // Radish QMS/SAP unavailable" used to).
  useEffect(() => {
    if (!submitting) {
      setSubmitElapsed(0);
      return;
    }
    const interval = setInterval(() => setSubmitElapsed((s) => s + 1), 1000);
    return () => clearInterval(interval);
  }, [submitting]);

  // Ticks while "Refresh Live Stock Now" (v2, Aug 2026) is in flight - a
  // fast, retried, quantities-only SAP pull (see
  // inventory_service.refresh_stock_quantities_only), deliberately NOT the
  // slow full refresh (with Standard Costs valuation) an earlier version
  // of this button used, which is what made it feel "stuck" for minutes.
  useEffect(() => {
    if (!refreshingStock) {
      setRefreshElapsed(0);
      return;
    }
    const interval = setInterval(() => setRefreshElapsed((s) => s + 1), 1000);
    return () => clearInterval(interval);
  }, [refreshingStock]);

  const submitProgressMessage = issueProgress && issueProgress.progress_total > 0
    ? `Moving stock for component ${issueProgress.progress_current} of ${issueProgress.progress_total}${issueProgress.current_component ? ` (${issueProgress.current_component})` : ""}...`
    : "Recording issued quantities and posting the SAP Goods Movement...";

  // Shared across all 3 loaders below (only one runs per 8s poll tick,
  // picked by viewMode) - a single transient network/Cloudflare blip on
  // this background poll used to pop a loud toast every single time,
  // even though the very next 8s tick almost always succeeds. Now it
  // fails silently (logged to console for diagnosis) and only surfaces a
  // toast once the SAME poll has failed 3 times in a row - a real,
  // persistent problem worth interrupting the user for, not one blip.
  const pollFailureCountRef = useRef(0);
  // Aug 2026 bug fix (user's own report): the Plant/Site filter used to
  // only list sites that happened to have a currently-open request, so a
  // real site like P9 silently vanished from the dropdown whenever it had
  // no pending request at that exact moment. Fetched once on mount from
  // the full inventory_cache instead (see server.py's /known-sites).
  const [knownSites, setKnownSites] = useState([]);
  useEffect(() => {
    axios.get(`${API}/store-requests/known-sites`).then(({ data }) => setKnownSites(data.sites || [])).catch(() => {});
  }, []);

  const loadRequests = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/store-requests`);
      setRequests(data.requests);
      pollFailureCountRef.current = 0;
    } catch (e) {
      pollFailureCountRef.current += 1;
      console.error("Failed to load pending stock requests:", e);
      if (pollFailureCountRef.current >= 3) {
        toast.error("Still unable to reach the server for the stock request queue - check your connection");
        pollFailureCountRef.current = 0; // re-arm for another 3 before nagging again during a longer outage
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const loadJournal = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/store-requests/journal`);
      setJournalRequests(data.requests);
      pollFailureCountRef.current = 0;
    } catch (e) {
      pollFailureCountRef.current += 1;
      console.error("Failed to load the requests journal:", e);
      if (pollFailureCountRef.current >= 3) {
        toast.error("Still unable to reach the server for the requests journal - check your connection");
        pollFailureCountRef.current = 0;
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Rule 2 (Aug 2026): requests where the store already issued a partial
  // quantity, the order proceeded, but a balance is still outstanding -
  // reopen one of these once more stock physically arrives in RM.
  const loadBalancePending = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/store-requests/balance-pending`);
      setBalanceRequests(data.requests);
      pollFailureCountRef.current = 0;
    } catch (e) {
      pollFailureCountRef.current += 1;
      console.error("Failed to load balance-pending requests:", e);
      if (pollFailureCountRef.current >= 3) {
        toast.error("Still unable to reach the server for balance-pending requests - check your connection");
        pollFailureCountRef.current = 0;
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const loadReturnJournal = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/store-returns/journal`);
      setReturnJournal(data.returns || []);
    } catch (e) {
      console.error("Failed to load the returns journal for Movement History:", e);
    }
  }, []);

  // Jun 2026 bug fix (user's report: an item with a SAP movement id on the
  // Movement Audit Trail showed as "No stock movements recorded yet" here).
  // Every filter is applied server-side over the full history, so the report
  // no longer depends on the browser having downloaded the entire request
  // journal first - the two reports now read the same data the same way.
  const loadMovementHistory = useCallback(async (silent = false) => {
    if (!silent) setMovementsLoading(true);
    try {
      const params = {};
      if (siteFilter !== "all") params.site_id = siteFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (searchTerm.trim()) params.search = searchTerm.trim();
      if (userSearch.trim()) params.person = userSearch.trim();
      const { data } = await axios.get(`${API}/store-requests/movement-history`, { params });
      setMovementHistory(data.movements || []);
      setMovementsError(false);
    } catch (e) {
      console.error("Failed to load movement history:", e);
      setMovementsError(true);
    } finally {
      setMovementsLoading(false);
    }
  }, [siteFilter, dateFrom, dateTo, searchTerm, userSearch]);

  useEffect(() => {
    const refresh = () => {
      if (viewMode === "queue") return loadRequests();
      if (viewMode === "balance") return loadBalancePending();
      if (viewMode === "movements") return undefined; // own effect below (server-side filters)
      if (viewMode === "journal") { loadJournal(); loadReturnJournal(); return; }
      return loadJournal();
    };
    refresh();
    const interval = setInterval(refresh, 8000);
    return () => clearInterval(interval);
  }, [viewMode, loadRequests, loadJournal, loadBalancePending, loadReturnJournal]);

  // Debounced so typing in Search/Requester doesn't fire a request per
  // keystroke; the background refresh is silent (never blanks the table).
  useEffect(() => {
    if (viewMode !== "movements") return undefined;
    const first = setTimeout(() => loadMovementHistory(), 350);
    const interval = setInterval(() => loadMovementHistory(true), 15000);
    return () => { clearTimeout(first); clearInterval(interval); };
  }, [viewMode, loadMovementHistory]);

  // Aug 2026 fix (testing_agent iteration_103): a request can be "issuing"
  // because a DIFFERENT session/tab started the job - pollIssueJob() only
  // exists for the tab that actually clicked submit, so without this the
  // detail view sat on "Issuing Stock..." forever despite promising it
  // would update automatically.
  useEffect(() => {
    if (!selected || selected.status !== "issuing" || submitting) return;
    const interval = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/store-requests/${selected._id}`);
        setSelected(data);
      } catch {
        // transient - next tick will retry
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [selected, submitting]);

  // Sep 14 2026 fix (real production incident, request 685734147/
  // P9-000121 - a component genuinely moved in SAP, then the background
  // job died and the request sat "Issuing Stock..." forever) - a
  // GENUINE in-progress job (another tab) almost always finishes within
  // a few components' worth of SAP calls, so only offer "Resume" after
  // a grace period, not immediately (avoids two tabs racing to resume
  // the same still-healthy job).
  useEffect(() => {
    setShowResumeOption(false);
    if (!selected || selected.status !== "issuing" || submitting) return;
    const timer = setTimeout(() => setShowResumeOption(true), 20000);
    return () => clearTimeout(timer);
  }, [selected?._id, selected?.status, submitting]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir(field === "created_at" ? "desc" : "asc");
    }
  };

  const rawList = viewMode === "queue" ? requests : viewMode === "balance" ? balanceRequests : journalRequests;
  const siteOptions = useMemo(() => {
    const fromRequests = rawList.map((r) => r.site_id).filter(Boolean);
    return Array.from(new Set([...knownSites, ...fromRequests])).sort();
  }, [rawList, knownSites]);

  const displayedRequests = useMemo(() => {
    let list = rawList.filter((r) => matchesSearch(r, searchTerm));
    if (statusFilter !== "all") list = list.filter((r) => r.status === statusFilter);
    if (siteFilter !== "all") list = list.filter((r) => r.site_id === siteFilter);
    const dir = sortDir === "asc" ? 1 : -1;
    list = [...list].sort((a, b) => {
      let va = a[sortField];
      let vb = b[sortField];
      if (sortField === "created_at") { va = new Date(va).getTime(); vb = new Date(vb).getTime(); }
      if (typeof va === "string") va = va.toLowerCase();
      if (typeof vb === "string") vb = vb.toLowerCase();
      if (va == null) return 1;
      if (vb == null) return -1;
      return va < vb ? -dir : va > vb ? dir : 0;
    });
    return list;
  }, [rawList, searchTerm, statusFilter, siteFilter, sortField, sortDir]);

  // Sep 10 2026: resolved/rejected Returns, surfaced as extra View-only
  // rows on the "Journal (All Requests)" tab only (queue/balance are
  // untouched - those are for OPEN store issue requests). Filters as
  // closely as sensibly possible to the same Search/Site/Status controls
  // already on this page - Status dropdown only lists issue-request
  // statuses, so "resolved"/"cancelled" are treated as this feature's
  // nearest equivalents (Resolved/Rejected); any other status hides
  // return rows since none of them apply to a return.
  const resolvedReturnRows = useMemo(() => {
    if (viewMode !== "journal") return [];
    let list = returnJournal.filter((r) => r.status === "resolved" || r.status === "rejected");
    list = list.filter((r) => returnMatchesSearch(r, searchTerm));
    if (siteFilter !== "all") list = list.filter((r) => r.site_id === siteFilter);
    if (statusFilter === "resolved") list = list.filter((r) => r.status === "resolved");
    else if (statusFilter === "cancelled") list = list.filter((r) => r.status === "rejected");
    else if (statusFilter !== "all") list = [];
    return list;
  }, [returnJournal, viewMode, searchTerm, siteFilter, statusFilter]);

  // Movement History rows: one per physically-issued component plus one per
  // resolved Return (negative qty, SFG -> RM/QC), built by the backend from
  // store_requests + store_returns - the same source the Movement Audit Trail
  // uses, with Site/From/To/Search/Requester already applied there.
  const movementRows = movementHistory;

  // Fetches the request behind /storeapproval/request/:requestId (direct
  // link, refresh, or browser back/forward all land here the same way -
  // no reliance on already having the row from a list fetch).
  useEffect(() => {
    if (!requestId) {
      setSelected(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/store-requests/${requestId}`);
        if (cancelled) return;
        setSelected(data);
        setResultMessage(null);
        // Aug 2026, user's explicit ask: leave Issued Qty blank rather
        // than prefilling the required/shortfall amount - the store
        // person types what they actually issue, validated against
        // usable RM stock below (see usableRmQty/overIssueErrors).
        // Oct 16 2026 exception, also the user's ask: an EA line whose SAP
        // requirement is fractional (0.512 of a pallet) opens with the
        // whole-unit default filled in, because that is what physically gets
        // issued - and the store can still edit it.
        const defaults = {};
        data.components.forEach((c) => {
          defaults[c.product_id] = c.issue_rounded_up && c.issued_qty == null ? String(c.suggested_issue_qty) : "";
        });
        setIssuedQty(defaults);
      } catch (err) {
        if (cancelled) return;
        if (err.response?.status === 403) {
          toast.error("You're not bound to this request's site - ask an admin to grant access on Access Management");
        } else {
          toast.error("Could not load that request - it may not exist anymore");
        }
        navigate(listPath(viewMode), { replace: true });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId]);

  const openRequest = (r) => {
    navigate(`/storeapproval/request/${r._id}?from=${viewMode}`);
  };

  const backToQueue = () => {
    navigate(listPath(viewMode));
    if (viewMode === "queue") loadRequests();
    else if (viewMode === "balance") loadBalancePending();
    else loadJournal();
  };

  // Sep 9 2026 - "Pending Store Return" is a self-contained panel with its
  // own queue/process/confirm/reject UI (see StoreReturnsPanel.jsx) - kept
  // as an early, separate return so the existing queue/journal/balance/
  // movements rendering below is completely untouched (user's explicit
  // "do not redesign this page" instruction).
  if (viewMode === "returns" && !requestId) {
    return (
      <div className="min-h-screen bg-[#F2F4F7] text-[#1D2939]">
        <Toaster position="top-right" />
        <header className="print:hidden min-h-16 bg-[#0E7C86] shadow-[0_1px_3px_0_rgba(16,24,40,0.15)] flex items-center justify-between px-3 sm:px-5 shrink-0 z-10 gap-2 sm:gap-4" data-testid="store-approval-header">
          <div className="flex items-center gap-3 shrink-0" data-testid="app-title">
            <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
              <Shield size={18} weight="fill" className="text-white" />
            </div>
            <div className="flex flex-col leading-tight">
              <span className="font-heading text-[16px] font-bold text-white tracking-tight">Production Control</span>
              <span className="font-sans text-[12px] text-white/70 hidden sm:inline">Store Approval</span>
            </div>
          </div>
          <div className="w-px h-7 bg-white/25 shrink-0" />
          <div className="flex items-center gap-3 flex-1 justify-start min-w-0">
            <NavTabs />
          </div>
          <SapConnectionStatus />
          <ErpConnectionStatus />
        </header>
        <main className="print:hidden max-w-[1400px] mx-auto p-4 sm:p-6 space-y-4">
          <StoreTabStrip viewMode={viewMode} navigate={navigate} />
          <StoreReturnsPanel siteFilter={siteFilter} storeActorName={storeActorName} />
        </main>
      </div>
    );
  }

  if (!selected) {
    if (requestId) {
      return (
        <div className="min-h-screen bg-[#F2F4F7] text-[#1D2939]">
          <Toaster position="top-right" />
          <main className="max-w-[1400px] mx-auto p-4 sm:p-6" data-testid="store-request-loading">
            <p className="text-sm text-[#667085] flex items-center gap-1.5"><ArrowClockwise size={14} className="animate-spin" /> Loading request {requestId}...</p>
          </main>
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-[#F2F4F7] text-[#1D2939]">
        <Toaster position="top-right" />
        <header className="print:hidden min-h-16 bg-[#0E7C86] shadow-[0_1px_3px_0_rgba(16,24,40,0.15)] flex items-center justify-between px-3 sm:px-5 shrink-0 z-10 gap-2 sm:gap-4" data-testid="store-approval-header">
          <div className="flex items-center gap-3 shrink-0" data-testid="app-title">
            <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
              <Shield size={18} weight="fill" className="text-white" />
            </div>
            <div className="flex flex-col leading-tight">
              <span className="font-heading text-[16px] font-bold text-white tracking-tight">Production Control</span>
              <span className="font-sans text-[12px] text-white/70 hidden sm:inline">Store Approval</span>
            </div>
          </div>
          <div className="w-px h-7 bg-white/25 shrink-0" />
          <div className="flex items-center gap-3 flex-1 justify-start min-w-0">
            <NavTabs />
          </div>
          <SapConnectionStatus />
          <ErpConnectionStatus />
        </header>
        <main className="print:hidden max-w-[1400px] mx-auto p-4 sm:p-6 space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <StoreTabStrip viewMode={viewMode} navigate={navigate} />
            <div>
              <Label className="text-xs font-bold text-[#344054]">Search</Label>
              <div className="relative">
                <MagnifyingGlass size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#98A2B3]" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={viewMode === "movements" ? "Search item / material / request ID..." : "Search material, site, requester, component..."}
                  className="w-64 bg-white pl-7"
                  data-testid="store-search-input"
                />
              </div>
            </div>
            {viewMode === "movements" && (
              <div>
                <Label className="text-xs font-bold text-[#344054]">Requester / Issued By</Label>
                <div className="relative">
                  <MagnifyingGlass size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#98A2B3]" />
                  <Input
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    placeholder="Search a name..."
                    className="w-56 bg-white pl-7"
                    data-testid="store-movements-user-search-input"
                  />
                </div>
              </div>
            )}
            {viewMode === "movements" && (
              <>
                <div>
                  <Label className="text-xs font-bold text-[#344054]">From</Label>
                  <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-36 bg-white" data-testid="store-movements-date-from" />
                </div>
                <div>
                  <Label className="text-xs font-bold text-[#344054]">To</Label>
                  <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-36 bg-white" data-testid="store-movements-date-to" />
                </div>
                {(dateFrom || dateTo) && (
                  <Button
                    variant="outline"
                    className="h-9"
                    onClick={() => { setDateFrom(""); setDateTo(""); }}
                    data-testid="store-movements-date-clear"
                  >
                    Clear Dates
                  </Button>
                )}
              </>
            )}
            {viewMode !== "movements" && (
              <div>
                <Label className="text-xs font-bold text-[#344054]">Status</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-44 bg-white" data-testid="store-status-filter-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all" data-testid="store-status-filter-all">All Statuses</SelectItem>
                    {Object.entries(STATUS_BADGE).map(([key, v]) => (
                      <SelectItem key={key} value={key} data-testid={`store-status-filter-${key}`}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-xs font-bold text-[#344054]">Plant / Site</Label>
              <Select value={siteFilter} onValueChange={setSiteFilter}>
                <SelectTrigger className="w-32 bg-white" data-testid="store-site-filter-trigger"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="store-site-filter-all">All Sites</SelectItem>
                  {siteOptions.map((s) => (
                    <SelectItem key={s} value={s} data-testid={`store-site-filter-${s}`}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {user?.role === "user" && (
              <div
                className="flex items-center gap-1.5 h-8 px-2.5 self-end text-xs font-medium text-[#175CD3] bg-[#EFF8FF] border border-[#B2DDFF] rounded-sm"
                data-testid="store-site-binding-notice"
              >
                <MapPin size={12} weight="bold" />
                {siteOptions.length > 0
                  ? `Restricted to: ${siteOptions.join(", ")}`
                  : "No site bound yet - ask an admin to assign one on Access Management"}
              </div>
            )}
            <Button variant="outline" onClick={() => (viewMode === "queue" ? loadRequests() : viewMode === "balance" ? loadBalancePending() : viewMode === "movements" ? loadMovementHistory(true) : loadJournal())} data-testid="store-refresh-button">
              <ArrowClockwise size={14} className="mr-1.5" /> Refresh
            </Button>
            {viewMode === "movements" && (
              <Button
                variant="outline"
                onClick={() => downloadCsv(
                  `stock-movement-history-${new Date().toISOString().slice(0, 10)}.csv`,
                  toCsv(movementRows, [
                    { label: "Request ID", get: (r) => r.request_id },
                    { label: "Issue ID", get: (r) => r.issue_id || "" },
                    { label: "Site", get: (r) => r.site_id },
                    { label: "Item", get: (r) => r.product_id },
                    { label: "Description", get: (r) => r.description || "" },
                    { label: "Qty Issued", get: (r) => r.issued_qty },
                    { label: "Unit", get: (r) => r.unit_of_measure || "" },
                    { label: "From Warehouse", get: (r) => r.warehouse || "" },
                    { label: "Owner", get: (r) => r.owner || "" },
                    { label: "To Bin", get: (r) => r.target_bin || "" },
                    { label: "Requested By", get: (r) => r.requester || "" },
                    { label: "Issued By", get: (r) => r.store_actor || "" },
                    { label: "SAP Movement Status", get: (r) => (!r.movement?.attempted ? "not moved" : r.movement.ok ? (r.movement.dry_run ? "Dry Run OK" : "Moved") : "Failed") },
                    { label: "Requested At", get: (r) => (r.requested_at ? new Date(r.requested_at).toLocaleString("en-IN") : "") },
                    { label: "Issued At", get: (r) => (r.when ? new Date(r.when).toLocaleString("en-IN") : "") },
                    { label: "Age at Issue", get: (r) => ageParts(r.requested_at, r.when)?.label || "" },
                  ]),
                )}
                data-testid="store-movements-download-csv"
              >
                <DownloadSimple size={14} className="mr-1.5" /> Download CSV
              </Button>
            )}
            <span className="text-xs text-[#667085] ml-auto" data-testid="store-result-count">
              {viewMode === "movements" ? `${movementRows.length} movement(s)` : viewMode === "journal" ? `${displayedRequests.length + resolvedReturnRows.length} request(s)${resolvedReturnRows.length ? ` (${resolvedReturnRows.length} return)` : ""}` : `${displayedRequests.length} request(s)`}
            </span>
          </div>

          {viewMode === "movements" ? (
            <div className="bg-white border border-[#D0D5DD] rounded-sm overflow-auto">
              <table className="w-full text-[12px] border-collapse" data-testid="store-movements-table">
                <thead>
                  <tr>
                    {["Request ID", "Issue ID", "Site", "Item", "Qty Issued", "From Warehouse", "To Bin", "Requested By", "Issued By", "SAP Movement", "Age at Issue", "When"].map((h) => (
                      <th key={h} className="bg-[#EAECF0] border border-[#D0D5DD] p-1.5 text-left text-xs font-bold text-[#344054] font-heading uppercase whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {movementRows.map((row, i) => (
                    <tr key={row.key} className={i % 2 === 0 ? "bg-white" : "bg-[#F9FAFB]"} data-testid={`store-movement-row-${i}`}>
                      <td className="border border-[#D0D5DD] px-2 py-1.5 font-mono font-bold text-[#175CD3]">{row.request_id}</td>
                      <td className="border border-[#D0D5DD] px-2 py-1.5 font-mono text-[#0E7C86]">{row.issue_id || "\u2014"}</td>
                      <td className="border border-[#D0D5DD] px-2 py-1.5">{row.site_id}</td>
                      <td className="border border-[#D0D5DD] px-2 py-1.5">
                        {row.is_return && <span className="mr-1.5 text-[9px] font-bold uppercase tracking-wide text-[#B54708] bg-[#FFFAEB] border border-[#FEDF89] rounded-sm px-1 py-0.5" data-testid={`store-movement-return-tag-${i}`}>Return</span>}
                        {row.product_id}{row.description ? ` - ${row.description}` : ""}
                      </td>
                      <td className="border border-[#D0D5DD] px-2 py-1.5 text-right tabular-nums">{formatQty(row.issued_qty)} {formatUnit(row.unit_of_measure)}</td>
                      <td className="border border-[#D0D5DD] px-2 py-1.5">{row.warehouse ? `${humanizeWarehouseId(row.warehouse)}${row.owner ? ` \u00b7 ${row.owner}` : ""}` : "\u2014"}</td>
                      <td className="border border-[#D0D5DD] px-2 py-1.5">{row.target_bin ? humanizeWarehouseId(row.target_bin) : "\u2014"}</td>
                      <td className="border border-[#D0D5DD] px-2 py-1.5">{row.requester || "\u2014"}</td>
                      <td className="border border-[#D0D5DD] px-2 py-1.5">{row.store_actor || "\u2014"}</td>
                      <td className="border border-[#D0D5DD] px-2 py-1.5 text-[11px]">
                        {!row.movement?.attempted ? (
                          <span className="text-[#98A2B3]" title={row.movement?.reason || ""}>not moved</span>
                        ) : row.movement.ok ? (
                          <span className="text-[#175CD3] font-bold">{row.movement.dry_run ? "Dry Run OK" : "Moved"} ({row.movement.external_id})</span>
                        ) : (
                          <span className="text-[#B42318]" title={[row.movement.error_detail, row.movement.error_hi].filter(Boolean).join(" / ") || (row.movement.faults || []).map((f) => f.note).join("; ") || ""}>Failed</span>
                        )}
                      </td>
                      <td className="border border-[#D0D5DD] px-2 py-1.5 whitespace-nowrap">
                        {ageParts(row.requested_at, row.when)?.label || "\u2014"}
                      </td>
                      <td className="border border-[#D0D5DD] px-2 py-1.5">{row.when ? new Date(row.when).toLocaleString("en-IN") : "\u2014"}</td>
                    </tr>
                  ))}
                  {movementRows.length === 0 && (
                    <tr><td colSpan={12} className="text-center py-8 text-[#98A2B3] border border-[#D0D5DD]" data-testid="store-movements-empty-state">
                      {/* Jun 2026: never claim "no record" when the report simply
                          has not loaded (or failed to) - that is exactly what sent
                          the user hunting for a movement the audit trail showed. */}
                      {movementsLoading ? "Loading movement history\u2026"
                        : movementsError ? "Couldn't load movement history - press Refresh to try again."
                        : "No stock movements match these filters."}
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          ) : (
          <div className="bg-white border border-[#D0D5DD] rounded-sm overflow-auto">
            <table className="w-full text-[13px] border-collapse" data-testid="store-requests-table">
              <thead>
                <tr>
                  <th className="bg-[#EAECF0] border border-[#D0D5DD] p-1.5 text-left text-xs font-bold text-[#344054] font-heading uppercase tracking-wide">Request ID</th>
                  <th className="bg-[#EAECF0] border border-[#D0D5DD] p-1.5 text-left text-xs font-bold text-[#344054] font-heading uppercase tracking-wide">Issue ID</th>
                  <SortableHeader label="Requested" field="created_at" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <th className="bg-[#EAECF0] border border-[#D0D5DD] p-1.5 text-left text-xs font-bold text-[#344054] font-heading uppercase tracking-wide">Age</th>
                  <SortableHeader label="Material" field="material_id" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <SortableHeader label="Site" field="site_id" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <SortableHeader label="Qty" field="quantity" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <SortableHeader label="Requester" field="requester" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <th className="bg-[#EAECF0] border border-[#D0D5DD] p-1.5 text-left text-xs font-bold text-[#344054] font-heading uppercase tracking-wide">Short Components</th>
                  <SortableHeader label="Status" field="status" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <th className="bg-[#EAECF0] border border-[#D0D5DD] p-1.5 text-left text-xs font-bold text-[#344054] font-heading uppercase tracking-wide"></th>
                </tr>
              </thead>
              <tbody>
                {displayedRequests.map((r, i) => (
                  <tr key={r._id} className={i % 2 === 0 ? "bg-white" : "bg-[#F9FAFB]"} data-testid={`store-request-row-${i}`}>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 font-mono font-bold text-[#175CD3] max-w-[110px] truncate" data-testid={`store-request-id-${i}`} title={r._id}>{r._id}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 font-mono text-[#0E7C86]" data-testid={`store-issue-id-${i}`}>{r.issue_id || "\u2014"}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">{new Date(r.created_at).toLocaleString("en-IN")}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">
                      {r.status === "pending" || r.status === "partial_pending_planner" ? (
                        <AgeBadge fromIso={r.created_at} testId={`store-age-${i}`} />
                      ) : (
                        <span className="text-[#667085]" data-testid={`store-age-${i}`} title="Time from request to resolution">{ageParts(r.created_at, r.resolved_at)?.label || "\u2014"}</span>
                      )}
                    </td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 font-medium">
                      {r.kind === "npd" ? (
                        <span className="inline-flex items-center gap-1.5" data-testid={`store-npd-tag-${i}`}>
                          <span className="text-[9px] font-bold uppercase tracking-wide text-[#5925DC] bg-[#F4F3FF] border border-[#D9D6FE] rounded-sm px-1 py-0.5">NPD</span>
                          {r.purpose === "consumption" ? "Production" : "NPD Project Consumption"}
                        </span>
                      ) : r.material_id}
                    </td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">{r.site_id}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 text-right tabular-nums">{formatQty(r.quantity)} {formatUnit(r.unit_code)}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">{r.requester}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">{r.components.length}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">
                      <Badge className={`${STATUS_BADGE[r.status]?.tone || ""} border`}>{STATUS_BADGE[r.status]?.label || r.status}</Badge>
                    </td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">
                      <Button size="sm" onClick={() => openRequest(r)} data-testid={`store-request-open-${i}`}>
                        {r.status === "pending" ? "Process" : "View"}
                      </Button>
                    </td>
                  </tr>
                ))}
                {viewMode === "journal" && resolvedReturnRows.map((r, i) => (
                  <tr key={`ret-${r._id}`} className={(displayedRequests.length + i) % 2 === 0 ? "bg-white" : "bg-[#F9FAFB]"} data-testid={`store-return-journal-row-${i}`}>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 font-mono font-bold text-[#175CD3] max-w-[110px] truncate" title={r.original_request_id || "MANUAL"}>{r.original_request_id || "MANUAL"}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 font-mono text-[#0E7C86]">
                      <span className="mr-1 text-[9px] font-bold uppercase tracking-wide text-[#B54708] bg-[#FFFAEB] border border-[#FEDF89] rounded-sm px-1 py-0.5" data-testid={`store-return-journal-tag-${i}`}>Return</span>
                      {r._id}
                    </td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">{new Date(r.created_at).toLocaleString("en-IN")}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5"><span className="text-[#667085]" title="Time from request to resolution">{ageParts(r.created_at, r.resolved_at)?.label || "\u2014"}</span></td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 font-medium">{(r.items || []).map((it) => it.product_id).join(", ")}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">{r.site_id}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 text-right tabular-nums">{formatQty((r.items || []).reduce((s, it) => s + (it.return_qty || 0), 0))}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">{r.requester}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">{(r.items || []).length}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">
                      <Badge className={`${r.status === "resolved" ? "bg-[#ECFDF3] text-[#027A48] border-[#ABEFC6]" : "bg-[#FEF3F2] text-[#B42318] border-[#FECDCA]"} border`}>{r.status === "resolved" ? "Resolved" : "Rejected"}</Badge>
                    </td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">
                      <Button size="sm" variant="outline" onClick={() => setViewingReturn(r)} data-testid={`store-return-journal-view-${i}`}>View</Button>
                    </td>
                  </tr>
                ))}
                {!loading && displayedRequests.length === 0 && (viewMode !== "journal" || resolvedReturnRows.length === 0) && (
                  <tr><td colSpan={11} className="text-center py-8 text-[#98A2B3] border border-[#D0D5DD]" data-testid="store-requests-empty-state">
                    {rawList.length === 0 ? (viewMode === "journal" ? "No requests recorded yet." : viewMode === "balance" ? "No requests with an outstanding balance right now." : "No pending stock requests right now.") : "No requests match your filters."}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
          )}
          {viewingReturn && (
            <ReturnDetailDialog ret={viewingReturn} onClose={() => setViewingReturn(null)} onPrint={() => { setPrintReturnTarget(viewingReturn); setViewingReturn(null); }} />
          )}
          {printReturnTarget && createPortal(<ReturnPrintSlip ret={printReturnTarget} />, document.body)}
        </main>
      </div>
    );
  }

  const isPending = selected.status === "pending";
  const isIssuing = selected.status === "issuing";
  const isNpdRequest = selected.kind === "npd";
  // Where this request's stock ends up: EVERY NPD request lands in the site's
  // NPD godown (Oct 16 2026 - NPD books the cost-centre consumption itself
  // afterwards, out of that godown); everything else goes RM -> SFG by the
  // fixed site rule.
  const targetWarehouseId = isNpdRequest ? selected.npd_target_warehouse : `${selected.site_id}/${selected.site_id}-SFG`;
  const movementColumnLabel = `Stock Movement (${isNpdRequest ? `RM \u2192 ${(targetWarehouseId || "").split("/").pop() || "NPD"}` : "RM \u2192 SFG"})`;
  // Sep 11 2026, user's explicit ask (real incident: legacy request
  // P9-000051 sitting in "Balance Pending" from BEFORE the Sep 11 rule
  // reversal, whose last movement actually succeeded - "Moved (275119)"
  // - yet the button still showed): don't trust the top-level status
  // alone for whether Retry makes sense - only show it if a component
  // with a real shortfall ACTUALLY has a failed/not-ok goods movement
  // on record. If every attempted movement genuinely succeeded, there's
  // nothing to retry regardless of what the overall status says.
  const hasFailedMovement = (selected.components || []).some((c) => c.shortfall > 0 && c.goods_movement && c.goods_movement.ok !== true);
  const isReopenable = selected.status === "resolved_balance_pending" && hasFailedMovement;
  const hasShortfall = isPending && selected.components.some((c) => {
    const q = Number(issuedQty[c.product_id]);
    return Number.isNaN(q) || q < c.required_qty;
  });

  // Aug 2026, user's explicit ask: "allowable" = usable RM stock only
  // (excludes Inspection/Restricted-Use rows, matches isLocationRestricted
  // used in the location breakdown above) - a store person should never
  // be able to type in more than what's actually free to issue from the
  // RM warehouse, regardless of how much production still needs.
  const rmWarehouseId = rmWarehouseIdForSite(selected.site_id);
  // NPD only: the store may issue from whichever zone actually holds the
  // item (user's explicit choice) - everything else stays RM-only.
  const sourceFor = (c) => (isNpdRequest ? (sourceWarehouse[c.product_id] || rmWarehouseId) : rmWarehouseId);
  const sourceOptionsFor = (c) => {
    // Oct 18 2026 (P1-N000005, IRON-SCR): SAP returns a "Not Assigned" row
    // with an EMPTY warehouse_id for stock that sits at the site with no
    // logistics area. Rendering it made Radix throw ("A <Select.Item /> must
    // have a value prop that is not an empty string") and the whole store
    // page went blank - and it is not a usable source anyway, a goods
    // movement needs a real logistics area.
    const ids = (c.locations || [])
      .filter((loc) => !isLocationRestricted(loc) && (Number(loc.qty) || 0) > 0 && (loc.warehouse_id || "").trim())
      .map((loc) => loc.warehouse_id.trim());
    return Array.from(new Set([rmWarehouseId, ...ids].filter(Boolean)));
  };
  const usableRmQty = (c) => (c.locations || [])
    .filter((loc) => loc.warehouse_id === sourceFor(c) && !isLocationRestricted(loc))
    .reduce((sum, loc) => sum + (Number(loc.qty) || 0), 0);
  const overIssueErrors = {};
  selected.components.forEach((c) => {
    const raw = issuedQty[c.product_id];
    if (raw === "" || raw == null) return;
    const q = Number(raw);
    const max = usableRmQty(c);
    if (!Number.isNaN(q) && q > max) overIssueErrors[c.product_id] = max;
  });
  const hasOverIssueError = Object.keys(overIssueErrors).length > 0;

  const submitIssue = async (decision) => {
    if (hasOverIssueError) {
      toast.error("One or more issued quantities exceed the usable stock available - fix them before submitting");
      return;
    }
    const issued = selected.components.map((c) => ({
      product_id: c.product_id,
      issued_qty: Number(issuedQty[c.product_id]) || 0,
      ...(isNpdRequest ? { source_warehouse_id: sourceFor(c) } : {}),
    }));
    if (issued.some((i) => Number.isNaN(i.issued_qty) || i.issued_qty < 0)) {
      toast.error("Issued quantities must be valid, non-negative numbers");
      return;
    }
    setSubmitting(true);
    try {
      // Aug 2026: this endpoint now returns almost instantly (job_id) - the
      // real SAP Goods Movement calls run in a background job, polled
      // below, so a request with several components can never trip the
      // platform's ingress/Cloudflare timeout the way one long synchronous
      // POST used to.
      const { data } = await axios.post(`${API}/store-requests/${selected._id}/issue`, {
        issued, decision: hasShortfall ? decision : null, actor: storeActorName,
      });
      setSelected(data.request);
      if (data.awaiting_receipt) {
        // NPD two-way approval: the movement is held until the requester
        // confirms what they physically received, so there is no job to poll.
        setSubmitting(false);
        setResultMessage(`Issue recorded - waiting for ${data.request?.requester || "the requester"} to confirm what they physically received. `
          + "Nothing has been posted to SAP yet; the goods movement fires the moment they confirm.");
        toast.success("Recorded - the requester has been emailed to confirm receipt");
        loadRequests();
        return;
      }
      await pollIssueJob(data.job_id, selected._id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit issued quantities");
      setSubmitting(false);
    }
  };

  const resumeIssue = async () => {
    setResuming(true);
    try {
      const { data } = await axios.post(`${API}/store-requests/${selected._id}/resume-issue`);
      setSubmitting(true);
      setShowResumeOption(false);
      await pollIssueJob(data.job_id, selected._id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to resume the stuck stock issue");
    } finally {
      setResuming(false);
    }
  };

  // "Refresh Live Stock Now" v2 (Aug 2026) - a fast, retried, quantities-
  // only SAP pull, then re-fetches THIS request so its component
  // locations pick up whatever was just found. Never blocks/breaks the
  // screen on failure - just toasts and leaves the existing data as-is.
  const refreshLiveStock = async () => {
    setRefreshingStock(true);
    setRefreshStatus(`Connecting to SAP for Site ${selected.site_id}...`);
    try {
      const { data } = await axios.post(`${API}/store-requests/refresh-live-stock`, null, { params: { site_id: selected.site_id } });
      setRefreshStatus(`Checking live stock at Site ${selected.site_id} (Raw Material & Quality Hold)...`);
      const job = await new Promise((resolve) => {
        const interval = setInterval(async () => {
          try {
            const { data: job } = await axios.get(`${API}/store-requests/refresh-live-stock/${data.job_id}`);
            if (job.status === "done" || job.status === "failed") {
              clearInterval(interval);
              resolve(job);
            }
          } catch {
            clearInterval(interval);
            resolve({ status: "failed", error: "Lost connection while refreshing live stock" });
          }
        }, 2000);
      });
      if (job.status === "failed") {
        toast.error(job.error || "Live stock refresh failed - still showing the last known data");
      } else {
        const found = job.result?.rows_found;
        setRefreshStatus(found != null ? `Found ${found} record(s) at Site ${selected.site_id} - updating table...` : "Updating table...");
        await new Promise((r) => setTimeout(r, 700));
        toast.success("Live SAP stock refreshed");
      }
      const { data: fresh } = await axios.get(`${API}/store-requests/${selected._id}`);
      setSelected(fresh);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start a live stock refresh");
    } finally {
      setRefreshingStock(false);
      setRefreshStatus(null);
    }
  };

  const pollIssueJob = (jobId, requestId) => new Promise((resolve) => {
    const interval = setInterval(async () => {
      try {
        const { data: job } = await axios.get(`${API}/store-requests/issue-status/${jobId}`);
        setIssueProgress(job);
        if (job.status === "done" || job.status === "failed") {
          clearInterval(interval);
          setSubmitting(false);
          setIssueProgress(null);
          const { data: freshRequest } = await axios.get(`${API}/store-requests/${requestId}`);
          setSelected(freshRequest);
          if (job.status === "failed") {
            toast.error(job.error || "Stock issue failed - the request has been reverted so you can retry");
          } else if (freshRequest.status === "resolved") {
            setResultMessage(freshRequest.resolution === "balance_completed"
              ? "Balance fully issued - this request is now closed."
              : freshRequest.resolution === "store_proceeded_partial" || freshRequest.resolution === "planner_approved_partial"
              ? "Partial stock issued - this request is now closed for good. The automated Production Order pipeline is resuming now."
              : "Stock issue recorded - the automated Production Order pipeline is resuming now.");
            toast.success(freshRequest.resolution === "balance_completed" ? "Balance fully issued - closed" : "Recorded - order creation resuming");
          } else if (freshRequest.status === "resolved_balance_pending") {
            setResultMessage("SAP rejected the movement - nothing actually posted, so this request stays open under \"Balance Pending\" until it's retried successfully.");
            toast.error("SAP rejected the movement - still open");
          } else if (freshRequest.status === "partial_pending_planner") {
            setResultMessage("Sent to the requester for approval - they'll decide whether to proceed with the partial stock.");
            toast.success("Sent to requester for approval");
          }
          resolve();
        }
      } catch {
        clearInterval(interval);
        setSubmitting(false);
        setIssueProgress(null);
        toast.error("Lost connection while tracking the stock issue - refresh to check its current status");
        resolve();
      }
    }, 2000);
  });

  return (
    <div className="min-h-screen bg-[#F2F4F7] text-[#1D2939]">
      <Toaster position="top-right" />
      <RequestPrintSlip request={selected} />
      <header className="print:hidden min-h-16 bg-[#0E7C86] shadow-[0_1px_3px_0_rgba(16,24,40,0.15)] flex items-center justify-between px-3 sm:px-5 shrink-0 z-10 gap-2 sm:gap-4">
        <div className="flex items-center gap-3 shrink-0" data-testid="app-title">
          <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
            <Shield size={18} weight="fill" className="text-white" />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-heading text-[16px] font-bold text-white tracking-tight">Production Control</span>
            <span className="font-sans text-[12px] text-white/70 hidden sm:inline">Store Approval</span>
          </div>
        </div>
        <div className="w-px h-7 bg-white/25 shrink-0" />
        <div className="flex items-center gap-3 flex-1 justify-start min-w-0">
          <NavTabs />
        </div>
        <div className="shrink-0 w-8" />
      </header>
      <main className="print:hidden max-w-[1400px] mx-auto p-4 sm:p-6 space-y-4">
        <button onClick={backToQueue} className="flex items-center gap-1.5 text-sm text-[#344054] hover:text-[#0E7C86]" data-testid="store-back-to-queue-button">
          <ArrowLeft size={14} weight="bold" /> Back to queue
        </button>

        <div className="bg-white border border-[#D0D5DD] rounded-sm p-4 space-y-3" data-testid="store-request-detail">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="font-heading text-sm font-bold text-[#1D2939] uppercase tracking-wide">
                {isNpdRequest ? (
                  <span className="inline-flex items-center gap-2" data-testid="store-npd-detail-tag">
                    <span className="text-[10px] font-bold uppercase tracking-wide text-[#5925DC] bg-[#F4F3FF] border border-[#D9D6FE] rounded-sm px-1.5 py-0.5">NPD</span>
                    {selected.purpose === "consumption" ? "Consumption Request" : "NPD Project Consumption Request"}
                    {" "}&middot; {selected.components.length} item(s)
                  </span>
                ) : (
                  <>{selected.material_id} &middot; {formatQty(selected.quantity)} {formatUnit(selected.unit_code)}</>
                )}
              </h3>
              <p className="text-xs text-[#667085]">
                Request ID <span className="font-mono font-bold text-[#175CD3]" data-testid="store-request-detail-id">{selected._id}</span>
                {selected.issue_id && (
                  <> &middot; Issue ID <span className="font-mono font-bold text-[#0E7C86]" data-testid="store-issue-detail-id">{selected.issue_id}</span></>
                )}
                {(() => {
                  // Only a real SAP Goods Movement ID proves stock left the
                  // store (Oct 2026, user: "no GAC id available for this
                  // transaction" on a request that showed as issued).
                  const gm = (selected.components || []).map((c) => c.goods_movement).find((m) => m?.ok && m?.external_id);
                  if (gm) return <> &middot; SAP GM <span className="font-mono font-bold text-[#027A48]" data-testid="store-sap-gm-id">{gm.external_id}</span></>;
                  if ((selected.issue_rounds || []).some((r) => !r.posted)) return <> &middot; <span className="font-bold text-[#B42318]" data-testid="store-no-sap-movement">No SAP movement posted yet</span></>;
                  return null;
                })()}
                {" "}&middot; Site {selected.site_id} &middot; Requested by {selected.requester}
                {isNpdRequest
                  ? <> &middot; Into {selected.npd_target_warehouse}{selected.customer_name ? <> &middot; Customer <strong data-testid="store-npd-customer">{selected.customer_name}{selected.customer_id ? ` (${selected.customer_id})` : ""}</strong></> : null}{selected.new_customer_name ? <> &middot; New customer <strong data-testid="store-npd-new-customer">{selected.new_customer_name}</strong></> : null}{selected.remark ? ` \u00b7 ${selected.remark}` : ""}</>
                  : <> &middot; Proposal {selected.production_proposal_id}</>}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Badge className={`${STATUS_BADGE[selected.status]?.tone || "bg-[#ECFDF3] text-[#027A48] border-[#ABEFC6]"} border`}>{STATUS_BADGE[selected.status]?.label || selected.status}</Badge>
              <Button variant="outline" size="sm" className="h-8 bg-white" onClick={() => window.print()} data-testid="store-print-request-button">
                <Printer size={14} className="mr-1.5" /> Print / Save as PDF
              </Button>
            </div>
          </div>

          {(isPending || isReopenable) && (
            <div className="flex flex-wrap items-center gap-3 bg-[#F9FAFB] border border-[#EAECF0] rounded-sm px-3 py-2" data-testid="store-refresh-live-stock-control">
              <Button
                variant="outline"
                size="sm"
                className="h-8 bg-white shrink-0"
                disabled={refreshingStock || submitting || isIssuing}
                onClick={refreshLiveStock}
                data-testid="store-refresh-live-stock-button"
                title="Just posted a Goods Receipt at this site? Pull live SAP quantities for THIS site now instead of waiting up to 30 min for the scheduled refresh"
              >
                <ArrowClockwise size={13} className={`mr-1.5 ${refreshingStock ? "animate-spin" : ""}`} />
                {refreshingStock ? `Refreshing (${refreshElapsed}s)...` : "Refresh Live Stock Now"}
              </Button>
              {refreshingStock ? (
                <div className="flex-1 min-w-[240px] max-w-md space-y-1">
                  <div className="h-1.5 w-full bg-[#EAECF0] rounded-full overflow-hidden">
                    <div className="h-full w-1/3 bg-[#0E7C86] rounded-full animate-[store-issue-progress_1.1s_ease-in-out_infinite]" />
                  </div>
                  <p className="text-[11px] text-[#667085]" data-testid="store-refresh-live-stock-status">{refreshStatus}</p>
                </div>
              ) : (
                <p className="text-[11px] text-[#667085]">Just posted a Goods Receipt? Pull live SAP quantities for this site before issuing.</p>
              )}
            </div>
          )}

          {resultMessage && (
            <div className="bg-[#ECFDF3] border border-[#ABEFC6] rounded-sm px-3 py-2 text-xs text-[#027A48]" data-testid="store-result-message">{resultMessage}</div>
          )}

          {selected.status === "awaiting_receipt" && (
            <div className="bg-[#FEF6EE] border border-[#F9DBAF] rounded-sm px-3 py-2 text-xs text-[#B93815] flex items-start gap-2" data-testid="store-awaiting-receipt-banner">
              <WarningCircle size={14} className="mt-0.5 shrink-0" />
              <span>
                Issued and handed over - <strong>nothing has posted to SAP yet</strong>. {selected.requester || "The requester"} has to
                confirm what they physically received; the goods movement fires at that moment. If they find a variance they will
                cancel this request with a remark and the stock stays yours.
              </span>
            </div>
          )}

          {selected.resolution === "cancelled_by_requester" && (
            <div className="bg-[#FEF3F2] border border-[#FECDCA] rounded-sm px-3 py-2 text-xs text-[#B42318] flex items-start gap-2" data-testid="store-requester-cancelled-banner">
              <WarningCircle size={14} className="mt-0.5 shrink-0" />
              <span>
                Cancelled by {selected.cancelled_by || selected.requester} after counting the material - nothing was posted to SAP, so
                this stock is still yours. <strong>Their remark:</strong> {selected.requester_cancel_remark || "—"}
              </span>
            </div>
          )}

          {selected.status === "partial_pending_planner" && !resultMessage && (
            <div className="bg-[#EFF8FF] border border-[#B2DDFF] rounded-sm px-3 py-2 text-xs text-[#175CD3] flex items-start gap-2" data-testid="store-awaiting-requester-banner">
              <WarningCircle size={14} className="mt-0.5 shrink-0" /> Already submitted - waiting for the requester to approve or reject this partial stock issue.
            </div>
          )}

          <div className="border border-[#D0D5DD] rounded-sm overflow-x-auto">
            <table className="w-full text-[12px] border-collapse" data-testid="store-detail-components-table">
              <thead>
                <tr>
                  {["Component", "Required Qty", "In Stock (Warehouse)", "Issued From", "Issued Qty", movementColumnLabel].map((h) => (
                    <th key={h} className="bg-[#EAECF0] border border-[#D0D5DD] p-1.5 text-left text-xs font-bold text-[#344054] font-heading uppercase whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {selected.components.map((c, i) => (
                  <tr key={c.product_id} className={i % 2 === 0 ? "bg-white" : "bg-[#F9FAFB]"}>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 align-top">
                      {c.product_id}{c.description ? ` - ${c.description}` : ""}
                      <MaterialTypeBadge isManufactured={c.is_manufactured} testId={`store-material-type-${i}`} />
                    </td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 text-right tabular-nums align-top">{formatQty(c.required_qty)} {formatUnit(c.unit_of_measure)}<RoundedUpFlag component={c} /></td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 text-right tabular-nums align-top" data-testid={`store-locations-${i}`}>
                      <LocationBreakdown locations={c.locations} unit={c.unit_of_measure} sourceWarehouseId={sourceFor(c)} />
                    </td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 align-top text-[11px]" data-testid={`store-issue-source-${i}`}>
                      {isNpdRequest && isPending ? (
                        /* NPD only - the store picks the godown the item is
                           actually taken from (default RM). */
                        <Select value={sourceFor(c)} onValueChange={(v) => setSourceWarehouse((prev) => ({ ...prev, [c.product_id]: v }))}>
                          <SelectTrigger className="h-7 w-48 bg-white text-[11px]" data-testid={`store-source-warehouse-trigger-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-white">
                            {sourceOptionsFor(c).map((id) => (
                              <SelectItem key={id} value={id} className="text-[11px]" data-testid={`store-source-warehouse-option-${i}-${id}`}>{humanizeWarehouseId(id)}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <>
                          {/* Aug 2026, user's fixed business rule - always Site RM -> Site SFG, no picker anymore */}
                          {humanizeWarehouseId(c.issued_from_warehouse || sourceFor(c))}
                          {c.issued_from_owner ? ` \u00b7 ${c.issued_from_owner}` : ""}
                        </>
                      )}
                    </td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 align-top">
                      {isPending ? (
                        <div className="space-y-0.5">
                          <Input
                            type="number"
                            placeholder="Enter qty..."
                            value={issuedQty[c.product_id] ?? ""}
                            onChange={(e) => setIssuedQty((prev) => ({ ...prev, [c.product_id]: e.target.value }))}
                            className={`h-7 w-28 text-right tabular-nums ${overIssueErrors[c.product_id] !== undefined ? "border-[#B42318] focus-visible:ring-[#B42318]" : ""}`}
                            data-testid={`store-issued-qty-input-${i}`}
                          />
                          {c.issue_rounded_up && (
                            <p className="text-[10px] text-[#B54708]" data-testid={`store-issue-rounded-hint-${i}`}>
                              Default {formatQty(c.suggested_issue_qty)} {formatUnit(c.unit_of_measure)} - a whole {formatUnit(c.unit_of_measure) || "unit"} for SAP's {formatQty(c.required_qty)}. Edit it if you issue less.
                            </p>
                          )}
                          {overIssueErrors[c.product_id] !== undefined && (
                            <p className="text-[10px] text-[#B42318] font-bold" data-testid={`store-issued-qty-error-${i}`}>
                              Exceeds usable stock ({formatQty(overIssueErrors[c.product_id])} {formatUnit(c.unit_of_measure)} available)
                            </p>
                          )}
                        </div>
                      ) : isReopenable && c.shortfall > 0 ? (
                        <div className="space-y-0.5" data-testid={`store-reopen-issue-cell-${i}`}>
                          <p className="text-[10px] text-[#667085]">Issued &amp; posted in SAP so far: {formatQty(c.issued_qty)} {formatUnit(c.unit_of_measure)}</p>
                          {c.rejected_qty_last_round > 0 && (
                            <p className="text-[10px] text-[#B42318] font-bold" data-testid={`store-rejected-last-round-${i}`}>
                              Last attempt of {formatQty(c.rejected_qty_last_round)} {formatUnit(c.unit_of_measure)} was rejected by SAP - nothing moved, re-enter below
                            </p>
                          )}
                          <Input
                            type="number"
                            placeholder="Enter qty..."
                            value={issuedQty[c.product_id] ?? ""}
                            onChange={(e) => setIssuedQty((prev) => ({ ...prev, [c.product_id]: e.target.value }))}
                            className={`h-7 w-28 text-right tabular-nums ${overIssueErrors[c.product_id] !== undefined ? "border-[#B42318] focus-visible:ring-[#B42318]" : ""}`}
                            data-testid={`store-issued-qty-input-${i}`}
                          />
                          {overIssueErrors[c.product_id] !== undefined && (
                            <p className="text-[10px] text-[#B42318] font-bold" data-testid={`store-issued-qty-error-${i}`}>
                              Exceeds usable stock ({formatQty(overIssueErrors[c.product_id])} {formatUnit(c.unit_of_measure)} available)
                            </p>
                          )}
                        </div>
                      ) : isReopenable ? (
                        <span className="tabular-nums text-[#027A48]" data-testid={`store-issued-qty-complete-${i}`}>{formatQty(c.issued_qty)} {formatUnit(c.unit_of_measure)} (fully issued)</span>
                      ) : (
                        <span className="tabular-nums">{formatQty(c.issued_qty)} {formatUnit(c.unit_of_measure)}</span>
                      )}
                    </td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 align-top text-[11px]" data-testid={`store-goods-movement-${i}`}>
                      {!c.goods_movement?.attempted ? (
                        <span className="text-[#98A2B3]" title={c.goods_movement?.reason || ""}>not moved{c.goods_movement?.reason ? ` - ${c.goods_movement.reason}` : ""}</span>
                      ) : c.goods_movement.ok ? (
                        <span className="text-[#175CD3] font-bold">{c.goods_movement.dry_run ? "Dry Run OK" : "Moved"} ({c.goods_movement.external_id})</span>
                      ) : (
                        <div className="text-[#B42318]" title={c.goods_movement.error_detail || ""}>
                          <p className="font-bold" data-testid={`store-goods-movement-error-en-${i}`}>Failed - {c.goods_movement.error || (c.goods_movement.faults || []).map((f) => f.note).join("; ") || "unknown error"}</p>
                          {c.goods_movement.error_hi && (
                            <p className="text-[#912018]" lang="hi" data-testid={`store-goods-movement-error-hi-${i}`}>{c.goods_movement.error_hi}</p>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {isPending && !resultMessage && (
            <div className="space-y-2">
              <div className="bg-[#F0FDF9] border border-[#A6F4C5] rounded-sm px-3 py-2 text-xs text-[#027A48]" data-testid="store-issue-movement-notice">
                <>Issuing stock records a SAP Goods Movement into <strong>{humanizeWarehouseId(targetWarehouseId)}</strong>{isNpdRequest ? " from the godown selected per line above" : <> from <strong>{humanizeWarehouseId(rmWarehouseId)}</strong> (fixed by site - not user-chosen)</>}. This is LIVE - stock physically moves in SAP the moment you confirm.{isNpdRequest ? <> NPD consumes it from there against cost centre <strong>{selected.npd_cost_center_id}</strong>.</> : null}</>
              </div>
              {hasShortfall ? (
                <>
                  <p className="text-[11px] text-[#B54708]">
                    {isNpdRequest
                      ? "Less than the requested quantity is entered - issuing now closes this NPD request for whatever the store actually gave."
                      : "One or more components are still short of the required quantity. Choose how to proceed:"}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button disabled={submitting || hasOverIssueError} onClick={() => submitIssue("proceed")} data-testid="store-submit-proceed-button">
                      Proceed to Issue
                    </Button>
                    {/* An NPD request has no requester-side approval screen -
                        user's rule is "issue what they have and close". */}
                    {!isNpdRequest && (
                      <Button variant="outline" disabled={submitting || hasOverIssueError} onClick={() => submitIssue("send_to_planner")} data-testid="store-submit-send-to-planner-button">
                        Send to Requester for Approval
                      </Button>
                    )}
                  </div>
                </>
              ) : (
                <Button disabled={submitting || hasOverIssueError} onClick={() => submitIssue(null)} data-testid="store-submit-full-button">
                  Confirm Stock Fully Issued
                </Button>
              )}
            </div>
          )}

          {isReopenable && !resultMessage && (
            <div className="space-y-2">
              <div className="bg-[#FEF6EE] border border-[#F9DBAF] rounded-sm px-3 py-2 text-xs text-[#B93815]" data-testid="store-reopen-notice">
                SAP rejected the last attempt to move the short component(s) above - nothing actually posted, so this request stays open. Fix the underlying issue (e.g. stock status/negative stock) and try again.
              </div>
              <Button disabled={submitting || hasOverIssueError} onClick={() => submitIssue(null)} data-testid="store-submit-reopen-button">
                Retry Issue
              </Button>
            </div>
          )}

          {["admin", "super_admin"].includes(user?.role) && (
            <AdminAdjustmentPanel request={selected} onDone={async () => {
              const { data } = await axios.get(`${API}/store-requests/${selected._id}`);
              setSelected(data);
              loadRequests();
              loadBalancePending();
            }} />
          )}

          {(submitting || isIssuing) && !resultMessage && (
            <div className="bg-white border border-[#D0D5DD] rounded-sm px-3 py-3 space-y-2" data-testid="store-issue-progress">
              <div className="h-1.5 w-full bg-[#EAECF0] rounded-full overflow-hidden">
                <div className="h-full w-1/3 bg-[#0E7C86] rounded-full animate-[store-issue-progress_1.1s_ease-in-out_infinite]" />
              </div>
              <p className="text-xs text-[#344054] flex items-center gap-1.5" data-testid="store-issue-progress-message">
                <ArrowClockwise size={12} className="animate-spin text-[#0E7C86]" />
                {submitting ? submitProgressMessage : "A stock issue is already being processed for this request (started from another session) - this page will update automatically."}
                {submitting && <span className="text-[#98A2B3] tabular-nums ml-1" data-testid="store-issue-progress-elapsed">({submitElapsed}s)</span>}
              </p>
              {showResumeOption && !submitting && (
                <div className="bg-[#FFFAEB] border border-[#FEDF89] rounded-sm px-3 py-2 space-y-1.5" data-testid="store-issue-resume-notice">
                  <p className="text-xs text-[#93370D]">
                    Still stuck? Some components may have already moved in SAP while others haven't been attempted yet. Resuming
                    is safe - any component already moved will NOT be moved again.
                  </p>
                  <Button size="sm" disabled={resuming} onClick={resumeIssue} data-testid="store-resume-issue-button">
                    {resuming ? "Resuming..." : "Resume Stock Issue"}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
